/**
 * Strateji editörünün dünyası ile metre ve oyun koordinatı arasındaki köprü.
 *
 * Editörün dünyası 0..`canvasSize` (1080) birimdir ve tile piramidinin
 * tamamını kaplar; piramit üç haritada da 16.384 m (`MAP_WORLD_M`). Yani bir
 * dünya birimi ≈ 15,2 m. Hesap makinesi aynı piramidi 1 birim = 1 m ile
 * kullanır (`lib/gameMap.ts`); buradaki fonksiyonlar editörün ölçeğini o
 * sisteme bağlar ki ızgara, cetvel ve hesap makinesi aynı sayıları okusun.
 */
import type { GameCalibration } from './gameMap'
import { MAP_WORLD_M } from './mapZones'

/**
 * Katalog `game` alanı göndermezse kullanılan kalibrasyon. Piramidin kenarı
 * üç haritada da aynı (`backend/strategies/maps.py` → `TILE_BOUNDS`), yalnızca
 * oynanabilir alan haritaya göre değişir; o da burada piramidin tamamıdır.
 */
export const DEFAULT_CALIBRATION: GameCalibration = {
  tileBounds: { minX: -0.03, maxX: 163.81, minY: -0.01, maxY: 163.83 },
  bounds: { minX: -0.03, maxX: 163.81, minY: -0.01, maxY: 163.83 },
  metersPerUnit: 100,
}

/** Hotzone çapı, metre — oyundaki ele geçirme alanının ölçüsü. */
export const HOTZONE_DIAMETER_M = 170

/**
 * Hotzone'un görünüşü sabittir: oyundaki gibi sarı. Kayıttaki renk/kalınlık
 * yok sayılır, yani her slaytta ve her bakanda aynı çember görünür.
 */
export const HOTZONE_STYLE = { color: '#facc15', sw: 2, fillOpacity: 0.14 } as const

/** Bir dünya biriminin metre karşılığı. */
export function metersPerWorld(canvasSize: number): number {
  return MAP_WORLD_M / canvasSize
}

/** Oyun koordinatı (x) → editör dünyası. */
export function gameXToWorld(x: number, cal: GameCalibration, canvasSize: number): number {
  return ((x - cal.tileBounds.minX) * cal.metersPerUnit) / metersPerWorld(canvasSize)
}

/** Oyun koordinatı (y) → editör dünyası. Y ekseni ters: oyunda yukarı büyür. */
export function gameYToWorld(y: number, cal: GameCalibration, canvasSize: number): number {
  return ((cal.tileBounds.maxY - y) * cal.metersPerUnit) / metersPerWorld(canvasSize)
}

/** Editör dünyası → oyun koordinatı. */
export function worldToGame(
  p: { x: number; y: number },
  cal: GameCalibration,
  canvasSize: number,
): { x: number; y: number } {
  const m = metersPerWorld(canvasSize)
  return {
    x: cal.tileBounds.minX + (p.x * m) / cal.metersPerUnit,
    y: cal.tileBounds.maxY - (p.y * m) / cal.metersPerUnit,
  }
}

/** Kullanıcıya gösterilen mesafe: 1 km altı tam metre, üstü iki ondalık km. */
export function formatDistance(meters: number): string {
  return meters < 1000 ? `${Math.round(meters)} m` : `${(meters / 1000).toFixed(2)} km`
}
